// src/modules/treasury/components/DenominationCountForm.jsx
// Input form for physical cash denomination counting.
// Renders one row per denomination: count input + auto-calculated amount.
// Parent controls the counts state; this component is purely presentational + input.

import React from 'react';
import { Table, InputNumber, Typography, Divider } from 'antd';
import { DENOMINATION_NOTES } from '../constants/treasuryConstants';

const { Text } = Typography;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

/**
 * @param {object}   counts            Current count values: { note2000: 5, note500: 10, ... }
 * @param {function} onChange          Called with full updated counts object on any change
 * @param {boolean}  [disabled]        Disable all inputs
 * @param {number}   [expectedCash]    When provided, enables difference calculation
 * @param {boolean}  [hideExpectedCash]  Hide the Expected Cash summary row
 * @param {boolean}  [hideDifference]    Hide the Difference summary row
 */
export default function DenominationCountForm({ counts = {}, onChange, disabled = false, expectedCash, hideExpectedCash = false, hideDifference = false }) {
  const handleChange = (key, value) => {
    onChange({ ...counts, [key]: value || 0 });
  };

  const totalPhysical = DENOMINATION_NOTES.reduce(
    (sum, note) => sum + (counts[note.key] || 0) * note.value,
    0,
  );

  const hasDifference = expectedCash !== undefined;
  const difference = hasDifference ? parseFloat((totalPhysical - expectedCash).toFixed(2)) : null;

  const columns = [
    {
      title: 'Denomination',
      dataIndex: 'label',
      width: 120,
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Count',
      dataIndex: 'key',
      width: 160,
      render: (key) => (
        <InputNumber
          min={0}
          step={1}
          precision={0}
          style={{ width: 120 }}
          value={counts[key] || 0}
          onChange={(val) => handleChange(key, val)}
          disabled={disabled}
        />
      ),
    },
    {
      title: 'Amount (₹)',
      dataIndex: 'key',
      align: 'right',
      render: (key, row) => {
        const amt = (counts[key] || 0) * row.value;
        return amt > 0
          ? <Text>₹{fmt(amt)}</Text>
          : <Text type="secondary">—</Text>;
      },
    },
  ];

  const dataWithFooter = [
    ...DENOMINATION_NOTES,
    { key: '__total__', label: 'Total Physical Cash', value: null, isTotal: true },
    ...(hasDifference && !hideExpectedCash ? [{ key: '__diff__', label: 'Expected Cash', value: null, isDiff: true }] : []),
    ...(hasDifference && !hideDifference   ? [{ key: '__gap__', label: 'Difference', value: null, isGap: true }] : []),
  ];

  return (
    <Table
      dataSource={dataWithFooter}
      columns={[
        {
          title: 'Denomination',
          dataIndex: 'key',
          width: 120,
          render: (key, row) => {
            if (row.isTotal) return <Text strong style={{ color: '#262626' }}>Total Physical Cash</Text>;
            if (row.isDiff) return <Text type="secondary">Expected Cash</Text>;
            if (row.isGap) return <Text strong>Difference</Text>;
            return <Text strong>{row.label}</Text>;
          },
        },
        {
          title: 'Count',
          dataIndex: 'key',
          width: 160,
          render: (key, row) => {
            if (row.isTotal || row.isDiff || row.isGap) return null;
            return (
              <InputNumber
                min={0}
                step={1}
                precision={0}
                style={{ width: 120 }}
                value={counts[key] || 0}
                onChange={(val) => handleChange(key, val)}
                disabled={disabled}
              />
            );
          },
        },
        {
          title: 'Amount (₹)',
          dataIndex: 'key',
          align: 'right',
          render: (key, row) => {
            if (row.isTotal) {
              return (
                <Text strong style={{ fontSize: 15 }}>
                  ₹{fmt(totalPhysical)}
                </Text>
              );
            }
            if (row.isDiff) {
              return <Text style={{ fontSize: 13 }}>₹{fmt(expectedCash)}</Text>;
            }
            if (row.isGap) {
              if (difference === 0) {
                return <Text style={{ color: '#389e0d', fontWeight: 600 }}>₹0 ✓</Text>;
              }
              return (
                <Text style={{ color: '#cf1322', fontWeight: 600 }}>
                  {difference > 0 ? '+' : ''}₹{fmt(difference)}
                </Text>
              );
            }
            const amt = (counts[key] || 0) * row.value;
            return amt > 0
              ? <Text>₹{fmt(amt)}</Text>
              : <Text type="secondary">—</Text>;
          },
        },
      ]}
      rowKey="key"
      size="small"
      pagination={false}
      rowClassName={(row) => {
        if (row.isTotal) return 'denomination-total-row';
        if (row.isGap && difference !== 0) return 'denomination-diff-row';
        return '';
      }}
      style={{ maxWidth: 480 }}
    />
  );
}
